import { z } from 'zod';
/*
 * GET /meta/structure — the live shape of the Meta ad account: every campaign,
 * its ad sets and their ads, with how each one is set up (objective, budget,
 * optimisation goal, targeting) and what it has produced.
 *
 * The /ads tab answers "which ad performs"; this answers "what have we actually
 * built in Meta", so the Meta section can draw the account as a map and hold it
 * up against the blueprint. Read-only: nothing here writes to Meta.
 *
 * Money is native account currency (AED); `fxAedToNok` is the live rate the
 * server used, so the client converts once, in one place.
 */
const ok = z.literal(true);
/** What an entity has produced over a window. */
export const metaPerfSchema = z.object({
    spend: z.number(),
    impressions: z.number(),
    reach: z.number(),
    frequency: z.number().nullable(),
    clicks: z.number(), // link clicks
    leads: z.number(), // instant-form + pixel leads, deduplicated as Ads Manager shows them
    schedules: z.number(), // website bookings (pixel/CAPI Schedule)
    // creative diagnostics — 0 for image ads on the video fields
    outboundClicks: z.number(),
    engagement: z.number(),
    video3s: z.number(), // 3-second plays → hook rate = video3s / impressions
    thruplays: z.number(), // 15 s or complete → hold rate = thruplays / video3s
    p25: z.number(),
    p50: z.number(),
    p75: z.number(),
    p100: z.number(),
    watchSecImpr: z.number(), // Σ(avg watch s × impressions); ÷ watchImpr = weighted avg
    watchImpr: z.number(),
});
/** Meta's effective_status, collapsed to what a person needs to know. */
export const metaStatusSchema = z.enum(['active', 'paused', 'parent_paused', 'issue', 'archived']);
export const metaAdSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: metaStatusSchema,
    createdTime: z.string().nullable(),
    /** Read from the ad name — the API returns every creative as an asset feed. */
    format: z.enum(['video', 'image', 'unknown']),
    lifetime: metaPerfSchema,
    recent: metaPerfSchema,
});
export const metaAdsetSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: metaStatusSchema,
    createdTime: z.string().nullable(),
    optimizationGoal: z.string().nullable(),
    /** The pixel/CRM event the ad set optimises for (SCHEDULE, LEAD …), if any. */
    conversionEvent: z.string().nullable(),
    dailyBudget: z.number().nullable(), // AED, only when the budget lives on the ad set
    ageMin: z.number().nullable(),
    ageMax: z.number().nullable(),
    geo: z.string().nullable(), // human summary, e.g. "6 mi rundt 59.43, 10.67"
    advantageAudience: z.boolean().nullable(),
    lifetime: metaPerfSchema,
    recent: metaPerfSchema,
    ads: z.array(metaAdSchema),
});
export const metaCampaignSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: metaStatusSchema,
    createdTime: z.string().nullable(),
    objective: z.string().nullable(),
    dailyBudget: z.number().nullable(), // AED, when the budget lives on the campaign (CBO)
    budgetLevel: z.enum(['campaign', 'adset']),
    bidStrategy: z.string().nullable(),
    lifetime: metaPerfSchema,
    recent: metaPerfSchema,
    adsets: z.array(metaAdsetSchema),
});
export const metaStructureResponse = z.object({
    ok,
    fetchedAt: z.string(),
    account: z.object({ id: z.string(), name: z.string().nullable(), currency: z.string() }),
    fxAedToNok: z.number(),
    since: z.string(), // lifetime window start (YYYY-MM-DD)
    recentDays: z.number(),
    recentFrom: z.string(), // the chosen period («recent»), inclusive
    recentTo: z.string(),
    totals: z.object({ lifetime: metaPerfSchema, recent: metaPerfSchema }),
    campaigns: z.array(metaCampaignSchema),
});
/* GET /meta/ad/:id/preview?format= — Meta's rendered preview of one ad + its texts. */
export const metaPreviewFormatSchema = z.enum([
    'MOBILE_FEED_STANDARD',
    'INSTAGRAM_STANDARD',
    'INSTAGRAM_REELS',
    'INSTAGRAM_STORY',
    'FACEBOOK_REELS_MOBILE',
]);
export const metaAdPreviewResponse = z.object({
    ok,
    adId: z.string(),
    format: metaPreviewFormatSchema,
    iframeSrc: z.string().nullable(), // signed by Meta, short-lived — fetch on demand
    thumbnailUrl: z.string().nullable(),
    bodies: z.array(z.string()),
    titles: z.array(z.string()),
    adsManagerUrl: z.string(),
});
/* /meta/layout — where people dragged things on the board (offsets from the computed spot). */
export const metaLayoutResponse = z.object({
    ok,
    positions: z.record(z.object({ dx: z.number(), dy: z.number() })),
});
export const metaLayoutSaveResponse = z.object({ ok, saved: z.number(), cleared: z.number() });
export const metaLayoutResetResponse = z.object({ ok, removed: z.number() });
//# sourceMappingURL=meta-structure.js.map